import { client } from '@api/rest/client';
import { GeneralTooltip } from '@components/common/GeneralTooltip';
import MaterialIcon from '@components/common/MaterialIcon';
import ButtonT from '@components/tailwind-components/Button/ButtonT';
import type { InvalidDataSourceContent } from '@conversed/rest/src/contracts/data-sources/data-sources.dto';
import {
  type UploadDocumentCompletedData,
  type UploadDocumentErrorData,
  UploadDocumentJobStatus,
  type UploadDocumentProgressData,
  type UploadDocumentsProgressModalProps,
} from '@conversed/shared/src/central/dto/upload-documents.dto';
import { getErrorTranslationKey } from '@helpers/errorHelper';
import { useUploadDocumentsSocket } from '@hooks/dataSources/useUploadDocumentsSocket';
import { useCompanySelector } from '@hooks/useCompanySelector';
import uploadIcon from '@images/upload-file-icon.svg';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const UploadDocumentsProgressModal: React.FC<
  UploadDocumentsProgressModalProps
> = ({
  files,
  documentType,
  label,
  sharedWithCompanyIds,
  autoTraining,
  autoInclude,
  folderId,
  expiryDate,
  selectedLLMBlocks,
  onClose,
  onComplete,
}) => {
  const { t } = useTranslation();
  const [jobId, setJobId] = useState<string | null>(null);
  const [status, setStatus] = useState<UploadDocumentJobStatus>(
    UploadDocumentJobStatus.Initializing,
  );
  const [filesProcessed, setFilesProcessed] = useState(0);
  const [currentFile, setCurrentFile] = useState<string>('');
  const [successfulFiles, setSuccessfulFiles] = useState(0);
  const [failedFiles, setFailedFiles] = useState(0);
  const [fileErrors, setFileErrors] = useState<UploadDocumentErrorData[]>([]);
  const [statusMessage, setStatusMessage] = useState(
    t('upload-documents.progress.initializing'),
  );
  const [error, setError] = useState<string | null>(null);
  const totalFiles = files.length;
  const { selectedCompany } = useCompanySelector();

  const companyId = useMemo(() => {
    return selectedCompany?.id;
  }, [selectedCompany]);

  const handleProgress = useCallback(
    (data: UploadDocumentProgressData) => {
      if (!jobId || data.jobId !== jobId) {
        // Set jobId if this is the first progress update
        if (!jobId) {
          setJobId(data.jobId);
        } else {
          return; // Ignore if jobId doesn't match
        }
      }

      setStatus(data.status);
      setFilesProcessed(data.progress);
      setCurrentFile(data.currentFile || '');

      // Update status message based on current status
      if (data.status === UploadDocumentJobStatus.Uploading) {
        setStatusMessage(t('upload-documents.progress.uploading-in-progress'));
      }
    },
    [jobId, t],
  );

  const handleCompleted = useCallback(
    (data: UploadDocumentCompletedData) => {
      if (jobId && data.jobId !== jobId) {
        return;
      }

      setStatus(UploadDocumentJobStatus.Completed);
      setSuccessfulFiles(data.successfulFiles);
      setFailedFiles(data.failedFiles);
      setFilesProcessed(data.totalFiles);

      if (data.errors && data.errors.length > 0) {
        // Convert errors to UploadDocumentErrorData format
        const errorDataList: UploadDocumentErrorData[] = data.errors.map(
          (err) => ({
            jobId: data.jobId,
            errorCode: err.errorCode,
            errorData: err.errorData,
            fileName: err.fileName,
          }),
        );

        setFileErrors(errorDataList);
      }

      onComplete();
    },
    [jobId, onComplete],
  );

  const handleError = useCallback(
    (data: UploadDocumentErrorData) => {
      if (jobId && data.jobId !== jobId) {
        return;
      }

      if (data.fileName) {
        // Individual file error
        setFileErrors((prev) => [...prev, data]);
      } else {
        // Job-level error
        setStatus(UploadDocumentJobStatus.Failed);
        setError(data.errorCode);
      }
    },
    [jobId],
  );

  const { isConnected, startUpload } = useUploadDocumentsSocket(
    handleProgress,
    handleCompleted,
    handleError,
  );

  // Initiate upload when socket is connected
  useEffect(() => {
    if (!isConnected || !companyId) {
      return;
    }

    const uploadStarted = startUpload({
      companyId,
      files,
      documentType,
      sharedWithCompanyIds,
      label,
      autoTraining,
      autoInclude,
      folderId,
      expiryDate,
      selectedLLMBlocks,
    });

    if (!uploadStarted) {
      setStatus(UploadDocumentJobStatus.Failed);
      setError(t('upload-documents.error.socket-not-connected'));
    }
  }, [
    isConnected,
    companyId,
    files,
    documentType,
    startUpload,
    t,
    label,
    autoTraining,
    autoInclude,
    sharedWithCompanyIds,
    folderId,
    expiryDate,
    selectedLLMBlocks,
  ]);

  // Timeout handling - a dropped or delayed socket event is not proof the
  // upload actually failed. The job runs and finishes entirely server-side;
  // this modal only learns the outcome via PROGRESS/COMPLETED broadcasts,
  // which can go missing under load without the job itself failing (seen in
  // e2e: backend.log showed the job completing successfully in under 2
  // seconds while this modal never received a single PROGRESS event before
  // the 30s mark). Before declaring failure, check whether the uploaded
  // files actually landed - a real backend failure never creates that row,
  // so finding it there is conclusive either way.
  useEffect(() => {
    if (filesProcessed > 0) {
      return;
    }

    const uploadFailedTimeout = setTimeout(async () => {
      try {
        const { data: existingDataSources } =
          await client.dataSource.getAllDataSources();
        const uploadedFileNames = new Set(files.map((file) => file.name));
        const landedFileCount = existingDataSources.filter(
          (dataSource) =>
            dataSource.name != null && uploadedFileNames.has(dataSource.name),
        ).length;

        if (landedFileCount === files.length) {
          setStatus(UploadDocumentJobStatus.Completed);
          setSuccessfulFiles(files.length);
          setFailedFiles(0);
          setFilesProcessed(files.length);
          onComplete();
          return;
        }
      } catch (reconciliationError) {
        console.error(
          'Failed to reconcile upload status against the data sources list:',
          reconciliationError,
        );
      }

      setStatus(UploadDocumentJobStatus.Failed);
      setError(t('upload-documents.error.backend-unresponsive'));
    }, 30 * 1000);

    return () => {
      clearTimeout(uploadFailedTimeout);
    };
  }, [t, filesProcessed, files, onComplete]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const isInProgress =
    status === UploadDocumentJobStatus.Initializing ||
    status === UploadDocumentJobStatus.Uploading;

  const progressPercentage =
    totalFiles > 0 ? Math.round((filesProcessed / totalFiles) * 100) : 0;

  const getErrorDisplayMessage = (errorCode: string) => {
    const translationKey = getErrorTranslationKey(errorCode);
    return t(translationKey);
  };

  // Helper to format validation errors for tooltip
  const getValidationErrorDetails = (
    errorData: InvalidDataSourceContent | null | undefined,
  ): string => {
    if (!errorData || typeof errorData !== 'object') {
      return '';
    }

    try {
      const MAX_ROWS_TO_SHOW = 45; // Show first 45 rows

      const validationErrors = Object.entries(errorData)
        .filter(([_, value]) => {
          // Check if value is an array with items
          return Array.isArray(value) && value.length > 0;
        })
        .map(([field, items]) => {
          const count = items.length;
          const itemsToShow = items.slice(0, MAX_ROWS_TO_SHOW);

          const rowNumbers = itemsToShow
            .map((item) => {
              if (
                typeof item === 'object' &&
                item !== null &&
                'index' in item
              ) {
                return `${item.index}`;
              }
              if (typeof item === 'number') {
                return `${item}`;
              }
              return String(item);
            })
            .join(', ');

          // Add "and X more" if there are more rows
          const moreText =
            count > MAX_ROWS_TO_SHOW
              ? ` - and ${count - MAX_ROWS_TO_SHOW} pieces more...`
              : '';

          return `${field}: Row ${rowNumbers}${moreText}`;
        })
        .join('\n');

      return validationErrors;
    } catch (error) {
      console.error('Error formatting validation details:', error, errorData);
      return '';
    }
  };

  return (
    <div className='tailwind-modal-form'>
      <div className='tailwind-modal-body'>
        {/* Icon with status overlay */}
        <div className='flex justify-center mb-3'>
          <div className='relative'>
            <img
              src={uploadIcon}
              className='w-20 h-20'
              alt='upload-documents'
            />
            {status === UploadDocumentJobStatus.Completed && (
              <div className='flex items-center absolute top-0 bottom-auto right-[-5px] bg-green-500 rounded-full p-1 leading-none'>
                <MaterialIcon
                  icon='check'
                  classes={['text-white', 'text-lg', '!leading-none']}
                />
              </div>
            )}
            {status === UploadDocumentJobStatus.Failed && (
              <div className='flex items-center absolute top-0 bottom-auto right-[-5px] bg-red-500 rounded-full p-1 leading-none'>
                <MaterialIcon
                  icon='close'
                  classes={['text-white', 'text-lg', '!leading-none']}
                />
              </div>
            )}
          </div>
        </div>

        {/* Status content */}
        <div className='min-h-[200px] flex flex-col items-center justify-center'>
          {isInProgress && (
            <div className='text-center w-full'>
              <p className='text-base font-medium text-primary-base mb-4'>
                {statusMessage}
              </p>

              {/* Progress bar */}
              <div className='w-full bg-gray-300 rounded-full h-2 mb-4'>
                <div
                  className='bg-primary-base h-2 rounded-full transition-all duration-300'
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>

              {/* File counter */}
              <div className='bg-gray-100 rounded-lg p-4 mb-3'>
                <p className='text-4xl font-medium text-primary-base mb-1'>
                  {filesProcessed} / {totalFiles}
                </p>
                <p className='text-xs text-gray-600'>
                  {t('upload-documents.progress.files-processed')}
                </p>
              </div>

              {/* Current file */}
              {currentFile && (
                <p className='text-xs text-gray-500 truncate px-4'>
                  {t('upload-documents.progress.current-file')}: {currentFile}
                </p>
              )}
            </div>
          )}

          {status === UploadDocumentJobStatus.Completed && (
            <div className='text-center w-full'>
              <p className='text-base font-medium text-primary-base mb-2'>
                {t('upload-documents.progress.completed')}
              </p>
              <p className='text-tiny text-gray-500 mb-4'>
                {t('upload-documents.progress.completed-message')}
              </p>

              <div className='grid grid-cols-2 gap-4 mb-4'>
                <div className='bg-green-50 rounded-lg p-4'>
                  <p className='text-3xl font-medium text-green-600 mb-1'>
                    {successfulFiles}
                  </p>
                  <p className='text-xs text-gray-600'>
                    {t('upload-documents.progress.successful')}
                  </p>
                </div>
                <div className='bg-red-50 rounded-lg p-4'>
                  <p className='text-3xl font-medium text-red-600 mb-1'>
                    {failedFiles}
                  </p>
                  <p className='text-xs text-gray-600'>
                    {t('upload-documents.progress.failed')}
                  </p>
                </div>
              </div>

              {/* Show errors if any */}
              {fileErrors.length > 0 && (
                <div className='text-left max-h-[150px] overflow-y-auto bg-red-50 rounded-lg p-3'>
                  <p className='text-xs font-medium text-red-700 mb-2'>
                    {t('upload-documents.progress.errors')}:
                  </p>
                  {fileErrors.map((fileError, index) => {
                    const errorMessage = getErrorDisplayMessage(
                      fileError.errorCode,
                    );
                    const validationDetails = getValidationErrorDetails(
                      fileError.errorData,
                    );

                    return (
                      <div
                        key={index}
                        className='flex gap-0.5 items-center text-tiny text-red-600 mb-1 group relative cursor-default'>
                        <span className='font-normal'>
                          {fileError.fileName}:
                        </span>{' '}
                        {errorMessage}
                        {validationDetails && (
                          <GeneralTooltip
                            text={validationDetails}
                            parentClassName='flex'
                            className='right-6 max-w-[260px]'
                            placement='left'>
                            <MaterialIcon
                              icon='info'
                              classes={[
                                'text-red-500',
                                'text-sm',
                                '!leading-none',
                              ]}
                            />
                          </GeneralTooltip>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {status === UploadDocumentJobStatus.Failed && (
            <div className='text-center'>
              <p className='text-lg font-medium text-gray-800 mb-4'>
                {t('upload-documents.progress.failed')}
              </p>
              <p className='text-sm text-red-600 break-words px-4'>
                {error
                  ? getErrorDisplayMessage(error)
                  : t('upload-documents.progress.error-unknown')}
              </p>
            </div>
          )}
        </div>
      </div>

      {(status === UploadDocumentJobStatus.Completed ||
        status === UploadDocumentJobStatus.Failed) && (
        <div className='tailwind-modal-footer'>
          <ButtonT
            type='button'
            styling='primary'
            size='xs'
            rounding='md'
            onClick={handleClose}>
            {t('general.buttons.close')}
          </ButtonT>
        </div>
      )}
    </div>
  );
};
